import { Modal, Select, useToast } from '@orbind/ui'
import { useMemo, useState } from 'react'
import './compartilhar.css'

/**
 * COMPARTILHAR o quadro no estilo do Google Drive (SPEC-20260916-1527).
 *
 * Um diálogo só: campo para adicionar pessoas em cima, a lista de quem tem
 * acesso (avatar, nome, e-mail) com o PAPEL (Dono / Editor / Leitor / Remover)
 * e o MODO DE ACESSO (Kanban / Lista / Ambos) em cada linha, e embaixo o
 * "Acesso geral" (Restrito ou Qualquer pessoa do workspace, com papel). Cada
 * mudança grava na hora — como no Google, não há botão Salvar; "Concluído"
 * só fecha. Donos continuam donos (tb_kanban_board_owner): virar Dono ou
 * deixar de ser passa por `onSalvarDonos`.
 */

export type PessoaDoWorkspace = { id: string; name: string; email?: string | null; isActive: boolean }
export type PapelNoQuadro = 'editor' | 'viewer'
export type AcessoNoQuadro = 'kanban' | 'lista' | 'ambos'
export type PessoaDoQuadro = { memberId: string; role: PapelNoQuadro; access: AcessoNoQuadro }
export type AcessoGeral = { generalAccess: 'restricted' | 'workspace'; generalRole: PapelNoQuadro }

type PapelEscolhido = 'owner' | PapelNoQuadro | 'remover'

const PAPEIS: { value: PapelEscolhido; label: string; icon?: string }[] = [
  { value: 'owner', label: 'Dono', icon: 'fas fa-crown' },
  { value: 'editor', label: 'Editor', icon: 'fas fa-pen' },
  { value: 'viewer', label: 'Leitor', icon: 'fas fa-eye' },
  { value: 'remover', label: 'Remover acesso', icon: 'fas fa-user-minus' },
]
const ACESSOS: { value: AcessoNoQuadro; label: string; icon: string }[] = [
  { value: 'ambos', label: 'Kanban e Lista', icon: 'fas fa-th-large' },
  { value: 'kanban', label: 'Só o Kanban', icon: 'fas fa-columns' },
  { value: 'lista', label: 'Só a Lista', icon: 'fas fa-list' },
]
const GERAL: { value: 'restricted' | 'workspace'; label: string; icon: string }[] = [
  { value: 'restricted', label: 'Restrito', icon: 'fas fa-lock' },
  { value: 'workspace', label: 'Qualquer pessoa do workspace', icon: 'fas fa-building' },
]
const PAPEIS_GERAIS: { value: PapelNoQuadro; label: string; icon: string }[] = [
  { value: 'viewer', label: 'Leitor', icon: 'fas fa-eye' },
  { value: 'editor', label: 'Editor', icon: 'fas fa-pen' },
]

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/)
  return ((partes[0]?.[0] ?? '') + (partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '')).toUpperCase()
}

export function CompartilharQuadro({
  open,
  onClose,
  boardId,
  nome,
  pessoas,
  meuId,
  ownerIds,
  pessoasDoQuadro,
  acessoGeral,
  souDono,
  podeEditarPessoas,
  onSalvarPessoas,
  onSalvarDonos,
}: {
  open: boolean
  onClose: () => void
  boardId: string
  nome: string
  /** Todo mundo do workspace (para adicionar). */
  pessoas: PessoaDoWorkspace[]
  meuId: string
  ownerIds: string[]
  pessoasDoQuadro: PessoaDoQuadro[]
  acessoGeral: AcessoGeral
  souDono: boolean
  /** Dono, ou membro editor com a política "qualquer membro gerencia". */
  podeEditarPessoas: boolean
  onSalvarPessoas: (pessoas: PessoaDoQuadro[], geral?: Partial<AcessoGeral>) => Promise<unknown>
  onSalvarDonos: (ownerIds: string[]) => Promise<unknown>
}) {
  const toast = useToast()
  const [busca, setBusca] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const porId = useMemo(() => new Map(pessoas.map((p) => [p.id, p])), [pessoas])

  // linhas: donos primeiro, depois as pessoas, na ordem em que estão
  const linhas = useMemo(() => {
    const donos = ownerIds.map((id) => ({ id, papel: 'owner' as PapelEscolhido, acesso: 'ambos' as AcessoNoQuadro }))
    const outros = pessoasDoQuadro
      .filter((p) => !ownerIds.includes(p.memberId))
      .map((p) => ({ id: p.memberId, papel: p.role as PapelEscolhido, acesso: p.access }))
    return [...donos, ...outros]
  }, [ownerIds, pessoasDoQuadro])
  const jaTem = new Set(linhas.map((l) => l.id))

  const termo = busca.trim().toLowerCase()
  const sugestoes =
    termo === ''
      ? []
      : pessoas
          .filter((p) => p.isActive && !jaTem.has(p.id))
          .filter((p) => p.name.toLowerCase().includes(termo) || (p.email ?? '').toLowerCase().includes(termo))
          .slice(0, 8)

  const rodar = async (acao: () => Promise<unknown>, ok: string) => {
    if (ocupado) return
    setOcupado(true)
    try {
      await acao()
      toast.push({ tone: 'success', message: ok })
    } catch (erro) {
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível salvar.' })
    } finally {
      setOcupado(false)
    }
  }

  const adicionar = (p: PessoaDoWorkspace) => {
    setBusca('')
    void rodar(
      () => onSalvarPessoas([...pessoasDoQuadro.filter((x) => x.memberId !== p.id), { memberId: p.id, role: 'editor', access: 'ambos' }]),
      `${p.name} agora tem acesso como Editor.`,
    )
  }

  const mudarPapel = (id: string, papelAtual: PapelEscolhido, novo: PapelEscolhido) => {
    if (novo === papelAtual) return
    const pessoa = porId.get(id)?.name ?? 'A pessoa'
    const semEla = pessoasDoQuadro.filter((x) => x.memberId !== id)
    const acessoAtual = pessoasDoQuadro.find((x) => x.memberId === id)?.access ?? 'ambos'
    if (novo === 'owner') {
      // vira dono: sai da lista de pessoas e entra na de donos
      void rodar(async () => {
        await onSalvarDonos([...ownerIds.filter((o) => o !== id), id])
        if (semEla.length !== pessoasDoQuadro.length) await onSalvarPessoas(semEla)
      }, `${pessoa} agora é dono do quadro.`)
      return
    }
    if (papelAtual === 'owner') {
      if (novo === 'remover') {
        void rodar(() => onSalvarDonos(ownerIds.filter((o) => o !== id)), `${pessoa} deixou de ter acesso.`)
        return
      }
      // deixa de ser dono e vira editor/leitor
      void rodar(async () => {
        await onSalvarDonos(ownerIds.filter((o) => o !== id))
        await onSalvarPessoas([...semEla, { memberId: id, role: novo, access: 'ambos' }])
      }, `${pessoa} agora é ${novo === 'editor' ? 'Editor' : 'Leitor'}.`)
      return
    }
    if (novo === 'remover') {
      void rodar(() => onSalvarPessoas(semEla), `${pessoa} deixou de ter acesso.`)
      return
    }
    void rodar(
      () => onSalvarPessoas([...semEla, { memberId: id, role: novo, access: acessoAtual }]),
      `${pessoa} agora é ${novo === 'editor' ? 'Editor' : 'Leitor'}.`,
    )
  }

  const mudarAcesso = (id: string, acesso: AcessoNoQuadro) => {
    const atual = pessoasDoQuadro.find((x) => x.memberId === id)
    if (!atual || atual.access === acesso) return
    void rodar(
      () => onSalvarPessoas(pessoasDoQuadro.map((x) => (x.memberId === id ? { ...x, access: acesso } : x))),
      `${porId.get(id)?.name ?? 'A pessoa'} entra por: ${ACESSOS.find((a) => a.value === acesso)?.label ?? acesso}.`,
    )
  }

  const copiarLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/kanbans/${boardId}`)
      toast.push({ tone: 'success', message: 'Link copiado.' })
    } catch {
      toast.push({ tone: 'danger', message: 'Não foi possível copiar o link.' })
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Compartilhar “${nome}”`}
      width={600}
      footer={
        <div className="cq__rodape">
          <button type="button" className="ob-btn ob-btn--ghost cq__link" onClick={() => void copiarLink()}>
            <i className="fas fa-link" aria-hidden /> Copiar link
          </button>
          <button type="button" className="ob-btn ob-btn--primary" onClick={onClose}>
            Concluído
          </button>
        </div>
      }
    >
      <div className="cq">
        {/* ADICIONAR PESSOAS: busca por nome ou e-mail; escolher já grava como Editor */}
        <div className="cq__busca">
          <i className="fas fa-user-plus" aria-hidden />
          <input
            value={busca}
            placeholder={podeEditarPessoas ? 'Adicionar pessoas pelo nome ou e-mail' : 'Só os donos adicionam pessoas'}
            aria-label="Adicionar pessoas"
            disabled={!podeEditarPessoas || ocupado}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && sugestoes[0]) adicionar(sugestoes[0])
              if (e.key === 'Escape') setBusca('')
            }}
          />
          {sugestoes.length > 0 ? (
            <ul className="cq__sugestoes" role="listbox">
              {sugestoes.map((p) => (
                <li key={p.id}>
                  <button type="button" role="option" aria-selected={false} onClick={() => adicionar(p)}>
                    <span className="wi-avatar cq__av">{iniciais(p.name)}</span>
                    <span className="cq__quem">
                      <span className="cq__nome">{p.name}</span>
                      {p.email ? <span className="cq__mail">{p.email}</span> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <h3 className="cq__secao">Pessoas com acesso</h3>
        <ul className="cq__lista" role="list">
          {linhas.map((l) => {
            const p = porId.get(l.id)
            const ehDono = l.papel === 'owner'
            const souEu = l.id === meuId
            // dono muda tudo; membro editor (política 'members') mexe nas pessoas, não nos donos
            const podeMudarPapel = souDono || (podeEditarPessoas && !ehDono)
            return (
              <li key={l.id} className="cq__linha">
                <span className={`wi-avatar cq__av${ehDono ? ' cq__av--dono' : ''}`}>{iniciais(p?.name ?? '?')}</span>
                <span className="cq__quem">
                  <span className="cq__nome">
                    {p?.name ?? 'Pessoa'}
                    {souEu ? <span className="cq__voce"> (você)</span> : null}
                  </span>
                  {p?.email ? <span className="cq__mail">{p.email}</span> : null}
                </span>
                <span className="cq__acesso">
                  <Select<AcessoNoQuadro>
                    value={ehDono ? 'ambos' : l.acesso}
                    options={ACESSOS}
                    disabled={ehDono || !podeMudarPapel || ocupado}
                    onChange={(v) => mudarAcesso(l.id, v)}
                    aria-labelledby={undefined}
                  />
                </span>
                <span className="cq__papel">
                  <Select<PapelEscolhido>
                    value={l.papel}
                    // quem não é dono não promove ninguém a dono — a opção fica, apagada,
                    // para a linha do dono continuar mostrando "Dono"
                    options={PAPEIS.map((o) => (o.value === 'owner' && !souDono ? { ...o, disabled: true } : o))}
                    disabled={!podeMudarPapel || ocupado}
                    onChange={(v) => mudarPapel(l.id, l.papel, v)}
                  />
                </span>
              </li>
            )
          })}
          {linhas.length === 0 ? <li className="cq__vazio">Ninguém ainda. Adicione pessoas acima.</li> : null}
        </ul>

        <h3 className="cq__secao">Acesso geral</h3>
        <div className="cq__geral">
          <span className={`cq__geral-icone${acessoGeral.generalAccess === 'workspace' ? ' cq__geral-icone--aberto' : ''}`}>
            <i className={acessoGeral.generalAccess === 'workspace' ? 'fas fa-building' : 'fas fa-lock'} aria-hidden />
          </span>
          <div className="cq__geral-texto">
            <div className="cq__geral-controles">
              <Select<'restricted' | 'workspace'>
                value={acessoGeral.generalAccess}
                options={GERAL}
                disabled={!souDono || ocupado}
                onChange={(v) =>
                  void rodar(
                    () => onSalvarPessoas(pessoasDoQuadro, { generalAccess: v }),
                    v === 'workspace' ? 'Qualquer pessoa do workspace pode abrir este quadro.' : 'Só quem foi adicionado abre este quadro.',
                  )
                }
              />
              {acessoGeral.generalAccess === 'workspace' ? (
                <Select<PapelNoQuadro>
                  value={acessoGeral.generalRole}
                  options={PAPEIS_GERAIS}
                  disabled={!souDono || ocupado}
                  onChange={(v) =>
                    void rodar(
                      () => onSalvarPessoas(pessoasDoQuadro, { generalRole: v }),
                      v === 'editor' ? 'Quem entra pelo workspace pode editar.' : 'Quem entra pelo workspace só lê.',
                    )
                  }
                />
              ) : null}
            </div>
            <p className="cq__geral-desc">
              {acessoGeral.generalAccess === 'workspace'
                ? `Qualquer pessoa do workspace encontra e abre este quadro como ${acessoGeral.generalRole === 'editor' ? 'Editor' : 'Leitor'}.`
                : 'Só os donos e as pessoas adicionadas acima encontram e abrem este quadro.'}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  )
}
